import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backupGuardianApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { fetchFailureKind, FETCH_FAILURE_TEXT, formatAgeHe } from "./ScheduledChecksPage";

const SEV = {
  ok:       { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", badge: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "תקין", icon: "✅" },
  warning:  { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   badge: "bg-amber-100  text-amber-800  border-amber-200",  label: "אזהרה", icon: "⚠️" },
  critical: { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-800",     badge: "bg-red-100   text-red-800    border-red-200",    label: "קריטי", icon: "🚨" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

function fmtAge(hours) {
  if (hours == null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} דקות`;
  if (hours < 24) return `${hours.toFixed(1)} שעות`;
  return `${(hours / 24).toFixed(1)} ימים`;
}

export default function BackupGuardianPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [configDraft, setConfigDraft] = useState(null);
  const [checkMsg, setCheckMsg] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const {
    data: health,
    isLoading: healthLoading,
    isError: healthFailed,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ["backup-guardian-health"],
    queryFn: backupGuardianApi.health,
    staleTime: 60_000,
  });

  const {
    data: config,
    isLoading: configLoading,
    isError: configFailed,
    error: configError,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ["backup-guardian-config"],
    queryFn: backupGuardianApi.getConfig,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (config && !configDraft) setConfigDraft(config);
  }, [config, configDraft]);

  const checkMutation = useMutation({
    mutationFn: backupGuardianApi.check,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["backup-guardian-health"] });
      if (res.ticket_created) {
        setCheckMsg(`קריאת התראה #${res.ticket_number} נוצרה`);
      } else if (res.ticket_number) {
        setCheckMsg(`הבעיה כבר מדווחת בקריאה פתוחה #${res.ticket_number}`);
      } else {
        setCheckMsg(res.severity === "ok" ? "הגיבויים תקינים — לא נדרשת פעולה" : `נמצאה בעיה (${SEV[res.severity]?.label || res.severity}) — אוטו-קריאה לא פעילה`);
      }
    },
    onError: (err) => addNotification(
      fetchFailureKind(err) === "offline"
        ? "לא התקבלה תשובה מהשרת — בדיקת הגיבויים לא הושלמה"
        : err?.response?.data?.detail || "שגיאה בבדיקת הגיבויים",
      "error"
    ),
  });

  const configMutation = useMutation({
    mutationFn: backupGuardianApi.setConfig,
    onSuccess: () => {
      addNotification("הגדרות שמירת גיבויים נשמרו", "success");
      qc.invalidateQueries({ queryKey: ["backup-guardian-config"] });
      qc.invalidateQueries({ queryKey: ["backup-guardian-health"] });   // severity / max age were computed from the old config
    },
    onError: (err) => addNotification(
      fetchFailureKind(err) === "offline"
        ? "לא התקבלה תשובה מהשרת — ההגדרות לא נשמרו"
        : err?.response?.data?.detail || "שגיאה בשמירת הגדרות הגיבויים",
      "error"
    ),
  });

  const draft = configDraft || config || {};
  const sev = health ? (SEV[health.severity] || SEV.warning) : null;
  const healthAge = health?.checked_at ? formatAgeHe(health.checked_at, now) : null;

  return (
    <div className="max-w-4xl mx-auto space-y-5" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">שומר גיבויים 💾</h1>
          <p className="text-sm text-gray-500 mt-0.5">מעקב תקינות גיבויים ויצירת קריאות אוטומטיות</p>
        </div>
        <button
          onClick={() => { setCheckMsg(null); checkMutation.mutate(); }}
          disabled={checkMutation.isPending}
          aria-busy={checkMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {checkMutation.isPending ? "בודק..." : "בדוק עכשיו"}
        </button>
      </div>

      {checkMsg && (
        <div role="status" className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-2 text-sm">{checkMsg}</div>
      )}

      {/* A refresh that failed after a successful load is a stale status, not a current one. */}
      {healthFailed && health && (
        <div role="status" className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-2 text-sm">
          <span>הרענון האחרון של מצב הגיבויים נכשל — המוצג הוא מהבדיקה האחרונה שהצליחה.</span>
          <button type="button" onClick={() => refetchHealth()} className="text-brand-600 hover:underline shrink-0">
            נסה שוב
          </button>
        </div>
      )}

      {/* Health status */}
      {healthLoading ? (
        <div className="bg-white rounded-xl border p-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : health ? (
        <div className={`rounded-xl border p-5 ${sev.bg} ${sev.border}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">{sev.icon}</span>
            <div>
              <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full border ${sev.badge}`}>
                {sev.label}
              </span>
              <p className={`text-xs mt-1 ${sev.text} opacity-75`}>
                נבדק ב-{fmtDate(health.checked_at)}
                {healthAge && ` (לפני ${healthAge})`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: "מספר גיבויים", value: health.backup_count },
              { label: "גיבוי אחרון", value: fmtDate(health.last_backup) },
              { label: "גיל גיבוי", value: fmtAge(health.age_hours) },
              { label: "מקסימום גיל (שעות)", value: health.max_age_hours },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white/60 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-sm font-semibold ${sev.text}`}>{value}</p>
              </div>
            ))}
          </div>

          {health.findings.length > 0 && (
            <ul className="space-y-1">
              {health.findings.map((f, i) => (
                <li key={i} className={`text-sm flex items-start gap-2 ${sev.text}`}>
                  <span className="mt-0.5 shrink-0">•</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : healthFailed ? (
        // no card at all read as "nothing to report": say the status is unknown, name the
        // failure class, and offer the one safe action
        <div role="alert" className="rounded-xl border p-5 bg-gray-50 border-gray-300 text-gray-700 text-sm font-medium space-y-2">
          <div>לא ניתן לטעון את מצב הגיבויים — הסטטוס לא ידוע</div>
          <div className="font-normal">{FETCH_FAILURE_TEXT[fetchFailureKind(healthError)]}</div>
          <button type="button" onClick={() => refetchHealth()} className="text-brand-600 hover:underline font-normal">
            נסה שוב — מצב הגיבויים
          </button>
        </div>
      ) : null}

      {/* Config */}
      {!configLoading && configFailed && !config && (
        // no card at all read as "nothing to configure": say the load failed, name the class,
        // and offer the one safe action
        <div role="alert" className="rounded-xl border p-5 bg-gray-50 border-gray-300 text-gray-700 text-sm font-medium space-y-2">
          <div>לא ניתן לטעון את הגדרות שומר הגיבויים — לא ניתן לערוך אותן כרגע</div>
          <div className="font-normal">{FETCH_FAILURE_TEXT[fetchFailureKind(configError)]}</div>
          <button type="button" onClick={() => refetchConfig()} className="text-brand-600 hover:underline font-normal">
            נסה שוב — הגדרות הגיבויים
          </button>
        </div>
      )}
      {!configLoading && configFailed && config && (
        // the form still shows the last successful load — marked as exactly that, never as current
        <div role="status" className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-2 text-sm">
          <span>הרענון האחרון של ההגדרות נכשל — הטופס מציג את הטעינה האחרונה שהצליחה.</span>
          <button type="button" onClick={() => refetchConfig()} className="text-brand-600 hover:underline shrink-0">
            נסה שוב
          </button>
        </div>
      )}
      {!configLoading && config && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h2 className="font-semibold text-gray-700">הגדרות Guardian</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">גיל מקסימלי לגיבוי (שעות)</label>
              <input
                type="number" min="1" max="720"
                value={draft.max_age_hours ?? config.max_age_hours}
                onChange={(e) => setConfigDraft((prev) => ({ ...(prev || config), max_age_hours: parseInt(e.target.value, 10) || 25 }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">מינימום קבצי גיבוי</label>
              <input
                type="number" min="0" max="9999"
                value={draft.min_count ?? config.min_count}
                onChange={(e) => setConfigDraft((prev) => ({ ...(prev || config), min_count: parseInt(e.target.value, 10) || 0 }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.auto_ticket ?? config.auto_ticket}
              onChange={(e) => setConfigDraft((prev) => ({ ...(prev || config), auto_ticket: e.target.checked }))}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-gray-700">צור קריאת התראה אוטומטית כשיש בעיה</span>
          </label>

          <div className="flex justify-end">
            <button
              onClick={() => configMutation.mutate(draft)}
              disabled={configMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {configMutation.isPending ? "שומר..." : "שמור הגדרות"}
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        תצוגת מצב-על של כל תחומי השומר (סריקה, ממצאים, גיבויים, חיבורים) ב
        <Link to="/guardian" className="text-brand-600 hover:underline mx-1">מרכז השומרים</Link>
      </p>
    </div>
  );
}
